import { startTransition, useEffect, useMemo, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ImageGenerationWorkflowDialogs } from "@/components/image-generation/image-generation-workflow-dialogs";
import { TimelineCreateEventDialog } from "@/components/timeline/timeline-create-event-dialog";
import { DeleteTimelineEventDialog } from "@/components/timeline/delete-timeline-event-dialog";
import { TimelineDetailPanel } from "@/components/timeline/timeline-detail-panel";
import { TimelineEmptyState } from "@/components/timeline/timeline-empty-state";
import { TimelineEventEditor } from "@/components/timeline/timeline-event-editor";
import { TimelineEventList } from "@/components/timeline/timeline-event-list";
import { TimelineGraphicalView } from "@/components/timeline/timeline-graphical-view";
import { TimelineViewToggle } from "@/components/timeline/timeline-view-toggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  buildImageDraftSessionFromSource,
  buildTimelineEventImagePrompt,
  mapImageCitation,
} from "@/features/image-generation";
import { acceptStoryImportElement } from "@/features/story-import/api/storyImportClient";
import { isStoryImportReviewEditState } from "@/features/story-import/review/importReviewEdit";
import { buildTimelineGraphLayout } from "@/features/timeline";
import {
  buildImportedTimelineEventElementData,
  buildImportedTimelineEventView,
} from "@/features/timeline/timeline.client";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import { projectShellStore } from "@/stores/projectShellStore";
import { useProjectImageStore } from "@/stores/project-image-store";
import { selectActiveTimelineEvent, useProjectTimelineStore } from "@/stores/projectTimelineStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

function useViewportWidth() {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 1280 : window.innerWidth,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    function handleResize() {
      setViewportWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return viewportWidth;
}

export function ProjectTimelinePage() {
  const { t } = useTranslation();
  const imageCreditCost = useRuntimeCreditCost("image_generation");
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const importReviewEditState = useMemo(() => {
    if (
      !projectId
      || !isStoryImportReviewEditState(location.state)
      || location.state.projectId !== projectId
      || location.state.element.elementType !== "timeline_event"
    ) {
      return null;
    }

    return location.state;
  }, [location.state, projectId]);
  const applyGeneratedEventImage = useProjectTimelineStore((state) => state.applyGeneratedEventImage);
  const activeEvent = useProjectTimelineStore(selectActiveTimelineEvent);
  const createEvent = useProjectTimelineStore((state) => state.createEvent);
  const deleteDialog = useProjectTimelineStore((state) => state.deleteDialog);
  const deleteEvent = useProjectTimelineStore((state) => state.deleteEvent);
  const closeDeleteDialog = useProjectTimelineStore((state) => state.closeDeleteDialog);
  const dismissNotice = useProjectTimelineStore((state) => state.dismissNotice);
  const dismissSummaryPreview = useProjectTimelineStore((state) => state.dismissSummaryPreview);
  const hydrateWorkspace = useProjectTimelineStore((state) => state.hydrateWorkspace);
  const limitPrompt = useProjectTimelineStore((state) => state.limitPrompt);
  const linkSelectedEvent = useProjectTimelineStore((state) => state.linkSelectedEvent);
  const notice = useProjectTimelineStore((state) => state.notice);
  const openDeleteDialog = useProjectTimelineStore((state) => state.openDeleteDialog);
  const previewSelectedEventSummary = useProjectTimelineStore(
    (state) => state.previewSelectedEventSummary,
  );
  const approveSelectedEventSummary = useProjectTimelineStore(
    (state) => state.approveSelectedEventSummary,
  );
  const refreshWorkspace = useProjectTimelineStore((state) => state.refreshWorkspace);
  const removeLink = useProjectTimelineStore((state) => state.removeLink);
  const removeSelectedEventImage = useProjectTimelineStore((state) => state.removeSelectedEventImage);
  const reorderEvents = useProjectTimelineStore((state) => state.reorderEvents);
  const saveSelectedEvent = useProjectTimelineStore((state) => state.saveSelectedEvent);
  const selectEvent = useProjectTimelineStore((state) => state.selectEvent);
  const selectedEventId = useProjectTimelineStore((state) => state.selectedEventId);
  const setViewMode = useProjectTimelineStore((state) => state.setViewMode);
  const status = useProjectTimelineStore((state) => state.status);
  const summaryError = useProjectTimelineStore((state) => state.summaryError);
  const summaryPreview = useProjectTimelineStore((state) => state.summaryPreview);
  const summaryRequestState = useProjectTimelineStore((state) => state.summaryRequestState);
  const viewMode = useProjectTimelineStore((state) => state.viewMode);
  const workspace = useProjectTimelineStore((state) => state.workspace);
  const viewportWidth = useViewportWidth();
  const startDraftSession = useProjectImageStore((state) => state.startDraftSession);
  const creditBalance = useSubscriptionStore((state) => state.creditBalance);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailCollapsed, setDetailCollapsed] = useState(false);
  const importedTimelineEvent = useMemo(
    () =>
      importReviewEditState && projectId
        ? buildImportedTimelineEventView({
            displayTitle: importReviewEditState.element.displayTitle,
            elementData: importReviewEditState.element.elementData,
            projectId,
          })
        : null,
    [importReviewEditState, projectId],
  );
  const isImportReviewEditingEvent = Boolean(importedTimelineEvent);

  function renderTimelineEditor(event: typeof importedTimelineEvent | typeof activeEvent) {
    return (
      <TimelineEventEditor
        allowDelete={!importReviewEditState}
        event={event}
        linkOptions={workspace?.linkOptions ?? []}
        onAddLink={async (option) => {
          if (importReviewEditState) {
            return;
          }

          await linkSelectedEvent(option);
        }}
        onDelete={() => openDeleteDialog()}
        onGenerateImage={handleGenerateEventImage}
        onRemoveImage={async () => {
          if (importReviewEditState) {
            return;
          }

          await removeSelectedEventImage();
        }}
        onRemoveLink={async (linkId) => {
          if (importReviewEditState) {
            return;
          }

          await removeLink(linkId);
        }}
        onClose={importReviewEditState ? undefined : () => selectEvent(null)}
        onSave={importReviewEditState
          ? async (draft) => {
              await acceptStoryImportElement({
                editedElementData: buildImportedTimelineEventElementData(
                  draft,
                  importReviewEditState.element.elementData,
                ),
                elementId: importReviewEditState.element.id,
                jobId: importReviewEditState.importJobId,
              });
              navigate(importReviewEditState.reviewRoute, { replace: true });
            }
          : saveSelectedEvent}
      />
    );
  }

  useEffect(() => {
    if (!projectId) {
      return;
    }

    void hydrateWorkspace(projectId);
  }, [hydrateWorkspace, projectId]);

  const graphLayout = useMemo(
    () =>
      workspace
        ? buildTimelineGraphLayout({
            events: workspace.events,
            selectedEventId,
            tier: workspace.tier,
            viewportWidth: Math.max(viewportWidth - (detailCollapsed ? 104 : 360), 720),
          })
        : null,
    [detailCollapsed, selectedEventId, viewportWidth, workspace],
  );

  async function handleGenerateEventImage() {
    if (!projectId || !activeEvent || !workspace) {
      return;
    }

    const projectImageSettings = await projectShellStore
      .getState()
      .hydrateProjectImageSettings(projectId);

    if (!projectImageSettings) {
      return;
    }

    startDraftSession(
      buildImageDraftSessionFromSource({
        creditCost: imageCreditCost,
        creditsRemaining: creditBalance,
        existingImagePath: activeEvent.imagePath,
        projectId,
        projectImageSettings,
        prompt: buildTimelineEventImagePrompt({
          description: activeEvent.description,
          eraLabel: activeEvent.eraLabel,
          styleDescription: projectImageSettings.styleDescription,
          title: activeEvent.title,
        }),
        sourceCitations: [
          mapImageCitation({
            reason: "Selected timeline event for this prompt.",
            sourceKind: "timeline_event",
            sourceLabel: activeEvent.title,
            sourceRecordId: activeEvent.id,
          }),
        ],
        sourceId: activeEvent.id,
        sourceKind: "timeline_event",
        sourceTitle: activeEvent.title,
        tier: workspace.tier,
      }),
    );
  }

  async function handleCreateEvent(draft?: { description: string; eraLabel: string; title: string }) {
    const createdEvent = await createEvent(draft);

    setCreateDialogOpen(false);
    setDetailCollapsed(false);

    if (viewMode === "graphical") {
      selectEvent(createdEvent.id);
    }
  }

  if (!projectId) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">{t("projects.dashboard.missing")}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === "loading" && !workspace) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 px-6 py-10 text-sm text-muted-foreground">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          {t("timeline.page.loading")}
        </CardContent>
      </Card>
    );
  }

  if (status === "error" && !workspace) {
    return (
      <Card>
        <CardContent className="space-y-4 px-6 py-8">
          <h2 className="text-2xl text-foreground">{t("timeline.page.errorTitle")}</h2>
          <p className="text-muted-foreground">
            {notice ? t(notice.message) : t("timeline.messages.loadError")}
          </p>
          <Button onClick={() => void refreshWorkspace()} type="button" variant="outline">
            {t("timeline.actions.refresh")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!workspace) {
    return null;
  }

  return (
    <div className="project-shell-page">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <TimelineViewToggle onChange={setViewMode} value={viewMode} />
        {!importReviewEditState ? (
          <Button onClick={() => setCreateDialogOpen(true)} type="button">
            {t("timeline.actions.create")}
          </Button>
        ) : null}
      </div>

      {notice?.kind === "error" ? (
        <div className="project-shell-notice" role={notice.kind === "error" ? "alert" : "status"}>
          <div className="flex items-start justify-between gap-3">
            <p>{t(notice.message)}</p>
            <button
              className="text-muted-foreground transition-colors hover:text-foreground"
              onClick={dismissNotice}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      {importReviewEditState ? (
        <div
          className="rounded-[1.75rem] border border-emerald-200/70 bg-emerald-50/90 px-5 py-4 text-sm text-emerald-950 shadow-vellum"
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">{t("storyImport.actions.editBeforeAccept")}</p>
              <p>{t("storyImport.review.body")}</p>
            </div>
            <Button
              onClick={() => navigate(importReviewEditState.reviewRoute, { replace: true })}
              size="sm"
              type="button"
              variant="outline"
            >
              {t("storyImport.actions.backToImport")}
            </Button>
          </div>
        </div>
      ) : null}

      {limitPrompt ? (
        <div
          className="rounded-[1.75rem] border border-amber-300/60 bg-amber-50/90 px-5 py-4 text-sm text-amber-950 shadow-vellum"
          role="status"
        >
          <p className="font-semibold">{t("timeline.messages.limitReachedTitle")}</p>
          <p className="mt-1">
            {t("timeline.messages.limitReachedBody", {
              allowed: limitPrompt.allowedCount,
              current: limitPrompt.currentCount,
              recommendedTier: limitPrompt.recommendedTier,
              tier: limitPrompt.tier,
            })}
          </p>
        </div>
      ) : null}

      {isImportReviewEditingEvent ? (
        <section>
          {renderTimelineEditor(importedTimelineEvent)}
        </section>
      ) : workspace.events.length === 0 ? (
        <TimelineEmptyState onCreateEvent={() => setCreateDialogOpen(true)} />
      ) : viewMode === "list" ? (
        <section
          className={
            activeEvent
              ? "grid gap-8 xl:grid-cols-[minmax(0,52rem)_minmax(24rem,1fr)] xl:items-start"
              : "space-y-6"
          }
        >
          <TimelineEventList
            events={workspace.events}
            onReorderEvents={(orderedEventIds) => void reorderEvents(orderedEventIds)}
            onSelectEvent={selectEvent}
            selectedEventId={selectedEventId}
          />
          {activeEvent ? renderTimelineEditor(activeEvent) : null}
        </section>
      ) : (
        <section
          className={
            detailCollapsed
              ? "grid gap-6 xl:grid-cols-[minmax(0,1fr)_2.75rem]"
              : "grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]"
          }
        >
          <TimelineGraphicalView
            events={workspace.events}
            layout={graphLayout}
            onSelectEvent={selectEvent}
            selectedEventId={selectedEventId}
          />
          <TimelineDetailPanel
            collapsed={detailCollapsed}
            event={activeEvent}
            onApproveSummary={() => void approveSelectedEventSummary()}
            onDismissSummaryPreview={dismissSummaryPreview}
            onGenerateSummary={() => void previewSelectedEventSummary()}
            onOpenLink={(href) => {
              startTransition(() => {
                navigate(href);
              });
            }}
            onToggleCollapsed={() => setDetailCollapsed((current) => !current)}
            summaryError={summaryError}
            summaryPreview={summaryPreview}
            summaryRequestState={summaryRequestState}
            tier={workspace.tier}
          />
        </section>
      )}

      <DeleteTimelineEventDialog
        eventTitle={
          workspace.events.find((event) => event.id === deleteDialog.targetEventId)?.title ?? ""
        }
        onConfirm={() => {
          void deleteEvent();
        }}
        onOpenChange={(open) => {
          if (!open) {
            closeDeleteDialog();
          }
        }}
        open={deleteDialog.open}
        submitting={deleteDialog.submitting}
      />
      <ImageGenerationWorkflowDialogs
        onApproved={async (result) => {
          if (activeEvent && result.approvedAssetRef) {
            await applyGeneratedEventImage(
              activeEvent.id,
              result.approvedAssetRef,
              result.creditsRemaining ?? null,
            );
          }
        }}
      />
      <TimelineCreateEventDialog
        onCreate={(draft) => handleCreateEvent(draft)}
        onOpenChange={setCreateDialogOpen}
        open={createDialogOpen}
      />
    </div>
  );
}
